import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CardProps } from "../../types/components";
import { Image } from "../../api-client";
import noImage from "../../assets/png/no-image.png";
import { useProductDetailModal } from "../../context/ProductDetailModalContext";

type CardVariant = "default" | "small";

function Card({
  id,
  name,
  imageUrl,
  price,
  imageUrls,
  description,
  variant = "default",
}: CardProps & { imageUrls: Image[]; variant?: CardVariant }) {
  const location = useLocation();
  const navigate = useNavigate();
  const productDetailModal = useProductDetailModal();
  const [currentImage, setCurrentImage] = useState(
    imageUrls.length > 0 ? imageUrls[0].image : imageUrl || noImage,
  );
  const [fade, setFade] = useState(false);

  const handleClick = (id: number) => {
    if (productDetailModal) {
      productDetailModal.openProductDetail(id);
      return;
    }
    navigate(`/product-detail/${id}`);
  };

  useEffect(() => {
    if (imageUrls.length < 2) return;
    const initialDelay = Math.floor(Math.random() * 3000);
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        setFade(true);
        setTimeout(() => {
          setCurrentImage((prev) => {
            const idx = imageUrls.findIndex((img) => img.image === prev);
            return imageUrls[(idx + 1) % imageUrls.length].image;
          });
          setFade(false);
        }, 400);
      }, 10000);
      return () => clearInterval(interval);
    }, initialDelay);
    return () => clearTimeout(timeout);
  }, [imageUrls]);

  /** ── SMALL ── */
  if (variant === "small") {
    return (
      <div
        className="w-36 bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md cursor-pointer transition-all duration-300 group"
        onClick={() => handleClick(id)}
      >
        <div className="aspect-square bg-gray-50 overflow-hidden">
          <img
            src={currentImage}
            alt={name}
            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="px-3 py-2">
          <h2 className="text-xs font-semibold text-gray-900 truncate">{name}</h2>
          <p className="text-xs font-bold text-gray-900 mt-1">${price}</p>
        </div>
      </div>
    );
  }

  /** ── CATEGORY ── */
  if (location.pathname === "/category") {
    return (
      <div className="cursor-pointer group" onClick={() => handleClick(id)}>
        <div className="overflow-hidden rounded-xl bg-[#f5f5f5] aspect-[3/4]">
          <img
            src={currentImage}
            alt={name}
            className={`w-full h-full object-contain group-hover:scale-105 transition-all duration-700 ${
              fade ? "opacity-0" : "opacity-100"
            }`}
          />
        </div>
        <div className="pt-2 text-center">
          <h2 className="text-sm font-semibold text-gray-900 truncate">{name}</h2>
          <p className="text-sm font-bold text-gray-900">${price}</p>
        </div>
      </div>
    );
  }

  /** ── DEFAULT ── */
  return (
    <div
      className="group w-full cursor-pointer rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all duration-300"
      onClick={() => handleClick(id)}
    >
      {/* Image area */}
      <div className="relative overflow-hidden bg-[#f5f5f5] aspect-[3/4]">
        <img
          src={currentImage}
          alt={name}
          className={`w-full h-full object-contain transition-all duration-700 group-hover:scale-105 ${
            fade ? "opacity-0 scale-95" : "opacity-100 scale-100"
          }`}
        />

        {/* Quick View — slides up on hover */}
        <div className="absolute inset-x-0 bottom-0 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out">
          <div className="bg-black/85 backdrop-blur-sm py-3 text-center">
            <span className="text-white text-[0.6rem] font-bold tracking-[0.22em] uppercase">
              Quick View
            </span>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="px-4 pt-3 pb-4">
        <h3 className="text-sm font-semibold text-gray-900 truncate leading-snug">
          {name}
        </h3>

        {description && (
          <div
            className="content-html text-[0.68rem] text-gray-400 mt-0.5 line-clamp-1 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: description as string }}
          />
        )}

        <div className="flex items-center justify-between mt-3">
          <p className="text-base font-extrabold text-gray-900 tracking-tight">
            ${price}
          </p>
          <button
            className="text-[0.6rem] font-bold tracking-[0.18em] uppercase bg-red-600 text-white px-4 py-2 rounded-full hover:bg-black active:scale-95 transition-all duration-200"
            onClick={(e) => {
              e.stopPropagation();
              handleClick(id);
            }}
          >
            Buy
          </button>
        </div>
      </div>
    </div>
  );
}

export default Card;
